import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

// Per-account strategy editor: phase, targeting, volume, goal. Saving creates
// a new version (history kept). Untouched = the tool's default behaviour.
const PHASES = [
  ['karma', 'Karma farming', 'Fresh account: comments in big SFW subs until its comment karma target, then SFW posts of non-nude photos too until its post karma target; nothing NSFW until you move it on'],
  ['warm_up', 'Warm-up', 'Fresh account building karma: only easy subs, no promotion, judged by upvotes/removals'],
  ['post_warm_up', 'Post-warm-up', 'Karma exists; testing what works, Discovery aggressive'],
  ['ready', 'Ready', 'Full rotation'],
  ['paused', 'Paused', 'Nothing is scheduled; reports still run'],
];

// Where this account stands in its model (routes/accounts.js → helpers/siblings.js modelOf)
function modelLine(model, accountId) {
  if (!model) return <span style={{ color: '#666' }}>…</span>;
  if (!model.name) return <span>Unassigned: this account stands alone (no model rules)</span>;
  if (model.accounts.length < 2) return <span>The only account of {model.name}</span>;
  if (model.mainId === accountId) return <span>⭐ Main account of {model.name}</span>;
  const main = model.accounts.find(a => a.id === model.mainId);
  return main
    ? <span>👥 Second account of {model.name} (main: u/{main.username})</span>
    : <span style={{ color: '#f59e0b' }}>⚠ {model.name} has no main account yet: subs its accounts share are only flagged</span>;
}

const box = { background: '#0f0f0f', borderRadius: '8px', border: '1px solid #2a2a2a', padding: '12px', marginTop: '12px', width: '100%' };
const label = { fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' };
const chip = (on) => ({ fontSize: '12px', padding: '5px 10px', borderRadius: '14px', cursor: 'pointer', background: on ? '#1e3a5f' : '#1a1a1a', color: on ? '#fff' : '#888', border: `1px solid ${on ? '#3b82f6' : '#333'}` });
const num = { width: '70px', fontSize: '13px', padding: '4px 6px', background: '#141414', color: '#ddd', border: '1px solid #333', borderRadius: '6px' };

export default function StrategyPanel({ account, onClose }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/accounts/${account.id}/strategy`);
      setData(res.data);
      setForm(JSON.parse(JSON.stringify(res.data.current)));
    } catch (e) { setMsg('Could not load the strategy: ' + (e.response?.data?.error || e.message)); }
  };
  useEffect(() => { load(); }, [account.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return <div style={box}><span style={{ color: '#666', fontSize: '12px' }}>{msg || 'Loading strategy…'}</span></div>;

  const t = form.targeting, v = form.volume;
  const setT = (k, val) => setForm(f => ({ ...f, targeting: { ...f.targeting, [k]: val } }));
  const setV = (k, val) => setForm(f => ({ ...f, volume: { ...f.volume, [k]: val } }));
  const toggleT = (k) => setT(k, !t[k]);
  const karmaPhase = form.phase === 'karma';
  // Karma farming is SFW only (the server enforces it); leaving it brings back
  // the targets the account had before
  const setPhase = (k) => setForm(f => {
    if (f.phase !== 'karma' || k === 'karma') return { ...f, phase: k };
    const before = (data?.versions || []).find(ver => ver.phase !== 'karma' && ver.targeting);
    return { ...f, phase: k, targeting: before ? { ...f.targeting, ...before.targeting } : { ...f.targeting, nsfw: true, sfw: false } };
  });
  const karma = data?.karma;

  // The model's main account: a person's decision, saved on the model
  const makeMain = async () => {
    const model = data.model;
    const current = model.accounts.find(a => a.id === model.mainId);
    const others = model.accounts.filter(a => a.id !== account.id).map(a => `u/${a.username}`);
    const consequence = current
      ? `u/${current.username} becomes a second account: it stays out of u/${account.reddit_username}'s subs and of subs run by the same moderators.`
      : `${others.join(', ')} ${others.length > 1 ? 'become second accounts: they stay' : 'becomes a second account: it stays'} out of its subs and of subs run by the same moderators.`;
    if (!window.confirm(`Make u/${account.reddit_username} the main account of ${model.name}?\n\n${consequence}`)) return;
    try {
      await axios.put(`${API}/api/accounts/${account.id}/main-account`);
      await load();
      setMsg(`u/${account.reddit_username} is now the main account of ${model.name}`);
    } catch (e) { setMsg('Could not set the main account: ' + (e.response?.data?.error || e.message)); }
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await axios.put(`${API}/api/accounts/${account.id}/strategy`, form);
      setMsg(`Saved as version ${res.data.current.version} — ${res.data.summary}`);
      await load();
      // Left Karma farming: the approved list was cleared — Discovery finds
      // the new one from the model's content
      const list = res.data.list;
      if (list?.run_discovery) {
        setMsg(`Saved as version ${res.data.current.version}. Out of Karma farming: ${list.cleared} approved subs cleared${list.removed_posts ? ` (and ${list.removed_posts} scheduled posts)` : ''} — running Discovery for the new list…`);
        try {
          await axios.post(`${API}/api/discovery/discover`, { creator_id: account.creator_id, account_id: account.id });
          setMsg(`Saved as version ${res.data.current.version}. ${list.cleared} approved subs cleared; Discovery is done — its suggestions are in the Planner's Pending.`);
        } catch (e) { setMsg(`Saved, list cleared, but Discovery failed: ${e.response?.data?.error || e.message} — run it from the Planner.`); }
      } else if (list?.error) setMsg(`Saved as version ${res.data.current.version}, but the approved list wasn't cleared: ${list.error}`);
    } catch (e) { setMsg('Save failed: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
  };

  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>
          🎯 Strategy for u/{account.reddit_username}
          <span style={{ fontSize: '11px', color: data?.is_default ? '#f59e0b' : '#3b82f6', marginLeft: '8px' }}>
            {data?.is_default ? 'default (not set yet)' : `v${data?.current?.version} · ${data?.summary}`}
          </span>
        </div>
        {onClose && <button onClick={onClose} title="Close" style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>}
      </div>

      <div style={label}>Phase — you decide when the account moves on</div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {PHASES.map(([key, l, tip]) => (
          <button key={key} type="button" title={tip} onClick={() => setPhase(key)} style={chip(form.phase === key)}>{l}</button>
        ))}
      </div>

      {karmaPhase && (
        <div style={{ margin: '-4px 0 12px', padding: '10px', background: '#141414', border: '1px solid #2a2a2a', borderRadius: '8px' }}>
          <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '8px' }}>
            {!karma ? 'Karma not read yet — the scraper reads it every few hours.'
              : karma.stage === 'comments'
                ? `💬 Comments only: comment karma ${karma.maxComment} of ${v.comment_karma_target}. Then SFW posts start.`
                : `🌤 SFW posts + comments: comment karma ${karma.maxComment} (target ${v.comment_karma_target} reached), post karma ${karma.post} ${karma.post >= (v.post_karma_target ?? 1500) ? '— ready for NSFW (see the Planner card)' : `of ${v.post_karma_target ?? 1500} for NSFW`}.`}
          </div>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px', color: '#aaa' }}>
            <span title="Comments only until the account's comment karma gets here">Comment karma target <input type="number" min="10" value={v.comment_karma_target ?? 750} onChange={e => setV('comment_karma_target', parseInt(e.target.value) || 750)} style={num} /></span>
            <span title="A new account starts at 3 a day and adds 2 a day up to this">Comments/day <input type="number" min="1" max="50" value={v.comments_per_day ?? 10} onChange={e => setV('comments_per_day', parseInt(e.target.value) || 10)} style={num} /></span>
            <span title="Post karma (from the SFW posts after the comment karma target) at which the Planner suggests moving on to NSFW">Ready for NSFW at <input type="number" min="50" value={v.post_karma_target ?? 1500} onChange={e => setV('post_karma_target', parseInt(e.target.value) || 1500)} style={{ ...num, width: '80px' }} /> post karma</span>
          </div>
          <div style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>
            {/* raw text while typing; the server splits it into subs on save */}
            Comment in <input type="text" value={Array.isArray(t.comment_subs) ? t.comment_subs.join(', ') : (t.comment_subs || '')} placeholder="automatic: r/AskReddit + big SFW subs that fit the model"
              onChange={e => setT('comment_subs', e.target.value)}
              style={{ ...num, width: '360px' }} />
          </div>
        </div>
      )}

      <div style={label}>Model — its main account comes first; second accounts stay out of its subs and of subs run by the same moderators</div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px', fontSize: '12px', color: '#aaa' }}>
        {modelLine(data?.model, account.id)}
        {data?.model?.name && data.model.accounts.length > 1 && data.model.mainId !== account.id && (
          <button type="button" onClick={makeMain} style={chip(false)}>⭐ Make this the main account</button>
        )}
      </div>

      <div style={label}>Targets — hard rules (Discovery never sees the rest; the Planner flags what's outside)</div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
        {karmaPhase ? <span style={{ ...chip(true), cursor: 'default' }} title="Karma farming is SFW only">🌤 SFW only (Karma farming)</span> : <>
          <button type="button" onClick={() => toggleT('nsfw')} style={chip(t.nsfw)}>🔞 NSFW</button>
          <button type="button" onClick={() => toggleT('sfw')} style={chip(t.sfw)}>🌤 SFW</button>
        </>}
        <button type="button" onClick={() => toggleT('stories')} style={chip(t.stories)} title="Text-only subreddits — the VA writes the post">📝 Stories / text subs</button>
        <button type="button" onClick={() => toggleT('sellers_allowed_only')} style={chip(t.sellers_allowed_only)} title="Skip subs whose rules ban sellers">💰 Seller-friendly only</button>
        <button type="button" onClick={() => toggleT('include_non_creator_subs')} style={chip(t.include_non_creator_subs)} title="Subs that aren't for creators posting themselves (non-OC etc.) — warned, never blocked">🧑 Include non-creator subs</button>
      </div>

      <div style={label}>Preferences — pre-fill Discovery, can be overridden for one run</div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#888' }}>AI content:</span>
        {[['any', '🤖 any'], ['tolerable', '🤖 tolerable (skip "No AI")'], ['friendly_only', '🤖 AI-friendly only']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setT('ai', k)} style={chip(t.ai === k)}>{l}</button>
        ))}
        <span style={{ width: '12px' }} />
        <button type="button" onClick={() => toggleT('dating')} style={chip(t.dating)}>💞 Dating subs</button>
        <button type="button" onClick={() => toggleT('verified')} style={chip(t.verified)}>✅ Verified subs</button>
        <button type="button" onClick={() => toggleT('non_verified')} style={chip(t.non_verified)}>☑️ Non-verified subs</button>
      </div>

      <div style={label}>Volume</div>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px', fontSize: '12px', color: '#aaa' }}>
        <span>Posts/day <input type="number" min="1" max="60" value={v.posts_per_day} onChange={e => setV('posts_per_day', parseInt(e.target.value) || 1)} style={num} /></span>
        <span>Sessions/week <input type="number" min="1" max="7" value={v.sessions_per_week} onChange={e => setV('sessions_per_week', parseInt(e.target.value) || 7)} style={num} /></span>
        <button type="button" onClick={() => setV('include_profile_post', !v.include_profile_post)} style={chip(v.include_profile_post)}>📌 Profile post each session</button>
        <span title="An item posted this many times in 30 days rests until the month rolls over">Max uses per item / 30d <input type="number" min="1" max="20" value={v.max_uses_per_item_30d ?? 3} onChange={e => setV('max_uses_per_item_30d', parseInt(e.target.value) || 3)} style={num} /></span>
      </div>

      <div style={label}>Goal, in your words — goes into every AI prompt for this account</div>
      <textarea value={form.goal || ''} onChange={e => setForm(f => ({ ...f, goal: e.target.value }))} rows={2}
        placeholder='e.g. "Fanvue AI model. Grow subscribers via cosplay and gaming subs; avoid real-person amateur subs; test dating subs in September."'
        style={{ width: '100%', fontSize: '13px', padding: '8px', background: '#141414', color: '#ddd', border: '1px solid #333', borderRadius: '6px', marginBottom: '8px', boxSizing: 'border-box' }} />
      <div style={label}>Notes for the AI (optional)</div>
      <textarea value={form.ai_notes || ''} onChange={e => setForm(f => ({ ...f, ai_notes: e.target.value }))} rows={2}
        placeholder='e.g. "Never suggest feet subs. Prefer subs under 500K visitors."'
        style={{ width: '100%', fontSize: '13px', padding: '8px', background: '#141414', color: '#ddd', border: '1px solid #333', borderRadius: '6px', marginBottom: '8px', boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', gap: '14px', alignItems: 'center', fontSize: '12px', color: '#aaa', marginBottom: '10px' }}>
        <span>Reddit account created on <input type="date" value={form.account_created_on || ''} onChange={e => setForm(f => ({ ...f, account_created_on: e.target.value || null }))} style={{ ...num, width: '150px' }} /></span>
        <span style={{ color: '#666' }}>(optional: used until the scraper has read the account's own date)</span>
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save as new version'}</button>
        {onClose && <button className="btn-secondary" onClick={onClose}>Close</button>}
        {msg && <span style={{ fontSize: '12px', color: msg.startsWith('Save') && !msg.startsWith('Save failed') ? '#22c55e' : '#f59e0b' }}>{msg}</span>}
      </div>
      {data?.versions?.length > 1 && (
        <details style={{ marginTop: '10px' }}>
          <summary style={{ fontSize: '11px', color: '#888', cursor: 'pointer' }}>Version history ({data.versions.length})</summary>
          {data.versions.map(ver => (
            <div key={ver.id} style={{ fontSize: '11px', color: '#777', padding: '3px 0' }}>
              v{ver.version} · {new Date(ver.created_at).toLocaleDateString()} · {ver.phase} · {ver.goal ? `"${ver.goal.slice(0, 80)}"` : 'no goal'}
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
